import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, NgxsSimpleChange, Store } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { SurveyActions } from '../actions/survey.actions';
import { Survey } from '../models/survey.model';
import { SurveyService } from 'src/app/services/survey.service';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';
import { DateTime } from 'luxon';
import { StorageService } from 'src/app/services/storage.service';
import { app_date_time_format } from 'src/app/globals';

export class SurveyStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    surveys: Survey[]
}

@State<SurveyStateModel>({
    name: 'survey_entry',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        surveys: []
    }
})
@Injectable()
export class SurveyState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private surveyEntryService: SurveyService,        
        private networkService: NetworkService,
        private offline: OfflineService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<SurveyStateModel>) {   
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                 
                        await this.reloadStateFromStorage()   
                        this.store.dispatch(new SurveyActions.IncrementPendingCount())
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {            
        if (this.storageService.is_initialized) {      
            const state_model: SurveyStateModel = change.currentValue      
            this.storageService.set('surveys', JSON.stringify(state_model))    
        }      
        if (this.networkService.currentStatus === ConnectionStatusEnum.Online) {            
            if (change.previousValue && change.previousValue.pending_count < change.currentValue.pending_count) {                
                const state_model: SurveyStateModel = change.currentValue
                await this.offline.processPendingSurveys(state_model.surveys)                    
            } 
        }   
    }
    
    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('surveys');        
        if (json) {
            const state_model: SurveyStateModel = JSON.parse(json)            
            if (state_model) {                
                this.store.dispatch(new SurveyActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new SurveyActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        const objs = await this.surveyEntryService.getMine()    
        if (objs != null) {
          const many = Survey.buildMany(objs)                     
          this.store.dispatch(new SurveyActions.Merge(many))
          return true
        }
        return false
    }
  
    @Selector()
    static getLastUpdate(state: SurveyStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: SurveyStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: SurveyStateModel) {
        return state
    }

    @Selector()
    static getAll(state: SurveyStateModel) {
        return state.surveys
    }

    @Selector()
    static getOne(state: SurveyStateModel) {
        return (id: number) => {
            return state.surveys.filter(s => s.id === id)
        }
    }

    @Action(SurveyActions.Refresh)
    async refresh({patchState}: StateContext<SurveyStateModel>, {  }:SurveyActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(SurveyActions.IncrementPendingCount)
    incrementPendingCount({patchState, getState}: StateContext<SurveyStateModel>, {  }:SurveyActions.IncrementPendingCount) {     
        const state = getState()
        patchState({
            pending_count: state.pending_count + 1
        })
    }
    
    @Action(SurveyActions.Init)
    init({setState, getState}: StateContext<SurveyStateModel>, { payload }:SurveyActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new SurveyActions.Refresh())
    }
    
    @Action(SurveyActions.Set)
    set({setState, getState}: StateContext<SurveyStateModel>, { payload }:SurveyActions.Set) {      
        const state = getState()        
        setState({   
            is_refreshing: state.is_refreshing, 
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),         
            surveys: payload
        })
    }

    @Action(SurveyActions.Merge)
    merge({getState, setState}: StateContext<SurveyStateModel>, { payload }:SurveyActions.Merge) {   
        // This is to merge data from the API provided in payload, into the app state.
        // Basically, if we have server data, we trust that, so we want to replace the 
        // current app state with the payload, except:
        // 1. Any objects pending upload or delete need to be merged in, even if it no longer exists.
        // 2. Same goes for any with a service_entry that is pending approval upload.
        const state = getState()
        const oldSurveys = [...state.surveys]
        let newSurveys = [...payload]      

        // For each wor in state now...
        for (const survey_entry of oldSurveys) {     
            // If wor id is not in the list from the server       
            let newSurvey = payload.find(f => f.id === survey_entry.id)   
            if (!newSurvey) {                
                // If there are any pending save/delete, keep the entire object in state, else do not since no longer on server                
                if (survey_entry.is_pending_save || survey_entry.is_pending_delete) {                 
                    newSurveys.push(survey_entry)
                }
            } else {                
                // Object in state is in the payload from the server. 
                // 1. If anything is pending, we need to preserve the one in state, first removing the one that is from the server
                // 1. Else we leave the one from the server to set in the new state
                if (survey_entry.is_pending_save || survey_entry.is_pending_delete) {
                    // Remove one that is from the server
                    newSurveys = newSurveys.filter((obj) => {return obj !== newSurvey});                        
                    // Add the one from state back in
                    newSurveys.push(survey_entry)
                }
            }
        }

        setState({
            is_refreshing: false,
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),
            surveys: newSurveys
        })
    }

    @Action(SurveyActions.AddOne)
    addOne({getState, patchState}: StateContext<SurveyStateModel>, { payload }:SurveyActions.AddOne) {
        const state = getState()              
        patchState({
            surveys: [...state.surveys, payload]
        })
    }

    @Action(SurveyActions.UpdateOne)
    updateOne({setState}: StateContext<SurveyStateModel>, { payload }:SurveyActions.UpdateOne) {
        setState(
            patch<SurveyStateModel>
                ({
                    surveys: updateItem((item: Survey) => item.id === payload.id, 
                        patch<Survey>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(SurveyActions.DeleteOne)
    deleteOne({setState}: StateContext<SurveyStateModel>, { payload }:SurveyActions.DeleteOne) {
        setState(
            patch<SurveyStateModel>
                ({
                    surveys: updateItem((item: Survey) => item.id === payload.id, 
                        patch<Survey>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(SurveyActions.ApiAddedOne)
    apiAddedOne({ setState }: StateContext<SurveyStateModel>, { payload }:SurveyActions.ApiAddedOne) {         
        setState(
            patch<SurveyStateModel>
                ({
                    surveys: updateItem((item: Survey) => item.uuid === payload.uuid, 
                        patch<Survey>
                            ({
                                id: payload.id,
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(SurveyActions.ApiUpdatedOne)
    apiUpdatedOne({ setState }: StateContext<SurveyStateModel>, { payload }:SurveyActions.ApiUpdatedOne) {         
        setState(
            patch<SurveyStateModel>
                ({
                    surveys: updateItem((item: Survey) => item.id === payload.id, 
                        patch<Survey>
                            ({
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(SurveyActions.ApiDeletedOne)
    apiDeletedOne({ setState }: StateContext<SurveyStateModel>, { payload }:SurveyActions.ApiDeletedOne) {         
        setState(
            patch<SurveyStateModel>
                ({
                    surveys: removeItem((item: Survey) => item.id === payload.id),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }
}
